import * as echarts from "echarts";
import { getLatest, getSeries, getSignals, getKrw } from "./api";
import { buildOption, type ZoomWindow } from "./chart";
import { fmtPct, fmtTime, connectionLabel, percentile, sortedInsert } from "./format";
import type { Latest, Tick1m, Signal } from "../functions/_lib/types";

const chart = echarts.init(document.getElementById("chart")!);
const LISTING = Math.floor(Date.parse("2026-07-10T00:00:00Z") / 1000);

// historical premium distribution (since listing), for the percentile readout
let histPremiums: number[] = [];
let histMin = NaN, histMax = NaN;

// Chart history accumulates here. The first load pulls everything since listing;
// every refresh after that asks only for rows newer than what we already hold.
// Refetching the whole range each minute cost ~73k D1 rows read per call, so one
// open tab drained the 5,000,000 rows/day free budget in about 70 minutes.
let rows: Tick1m[] = [];
let lastTs = 0; // newest ts the server has handed us, counted before filtering

// --- i18n (English + Simplified Chinese) ---
type Lang = "zh-CN" | "en";
const I18N: Record<Lang, Record<string, string>> = {
  "zh-CN": { title: "SKHY 溢价套利追踪", h1: "SKHY / 000660 溢价", legs: "行情", signals: "信号", follow: "更多交易信息，关注", hist: "历史", range: "区间", toggle: "EN" },
  en: { title: "SKHY Premium Arbitrage", h1: "SKHY / 000660 Premium", legs: "Legs", signals: "Signals", follow: "More trading insights →", hist: "hist", range: "range", toggle: "中" },
};
let lang: Lang = (localStorage.getItem("lang") as Lang) || (navigator.language.startsWith("zh") ? "zh-CN" : "en");
const t = () => I18N[lang];

const MARKET_ZH: Record<string, string> = {
  "US regular": "美股盘中", "US pre-market": "美股盘前", "US after-hours": "美股盘后",
  "US overnight": "美股夜盘", "US closed": "美股休市", "KRX open": "韩股开盘", "KRX closed": "韩股收盘",
};
const marketState = (s: string) => (lang === "en" ? s : s.split(" · ").map((p) => MARKET_ZH[p] ?? p).join(" · "));

function applyI18n() {
  document.title = t().title;
  document.documentElement.lang = lang;
  document.querySelectorAll<HTMLElement>("[data-i18n]").forEach((el) => {
    const k = el.dataset.i18n!;
    if (t()[k]) el.textContent = t()[k];
  });
  const btn = document.getElementById("langtoggle");
  if (btn) btn.textContent = t().toggle;
}

function renderSignals(sigs: Signal[]) {
  const ul = document.getElementById("signals");
  if (!ul) return;
  ul.innerHTML = sigs
    .slice(0, 50)
    .map(
      (s) =>
        `<li><span class="mono">${fmtTime(s.ts)}</span> ` +
        `<span class="badge badge-${s.level}">${s.level}</span> ${fmtPct(s.premium_pct)}</li>`,
    )
    .join("");
}

// Drop points where BOTH markets are closed (weekends/holidays): the 24/7
// collector still emits a flat held value every minute, which otherwise shows
// as a long ugly flat stretch. Removing them lets the trading-time axis
// collapse the weekend entirely.
const isTradable = (r: Tick1m) => !(r.session === "closed" && r.krw_stale === 1);

/**
 * Where the viewer is currently looking, in category indices, so the 60s
 * refresh can put them back instead of snapping to the default window.
 * echarts reports dataZoom as percentages, so convert against the category
 * count of the render they were looking at.
 */
function currentZoom(): ZoomWindow | undefined {
  const opt = chart.getOption() as any;
  const dz = opt?.dataZoom?.[0];
  const n = opt?.xAxis?.[0]?.data?.length ?? 0;
  if (!dz || n < 2) return undefined; // first render — take the default window
  const idx = (p: number) => Math.round((p / 100) * (n - 1));
  const end = dz.end ?? 100;
  return { startIdx: idx(dz.start ?? 0), endIdx: end >= 99.99 ? null : idx(end) };
}

async function loadHistory() {
  // Quantise the window end to the minute: a raw Date.now() mints a unique URL
  // on every call, which misses both the browser cache and the edge cache.
  const to = Math.floor(Date.now() / 60000) * 60;
  const first = lastTs === 0;
  const fresh = await getSeries("1m", first ? LISTING : lastTs + 1, to);
  if (fresh.length) {
    lastTs = fresh[fresh.length - 1].ts;
    const added = fresh.filter(isTradable);
    // distribution for the percentile readout (T-trading: is current premium historically high?)
    const prem = added.map((r) => r.premium_close).filter((x): x is number => x != null);
    if (first) {
      rows = added;
      histPremiums = prem.sort((a, b) => a - b);
    } else {
      // a delta is a row or two, so splicing it in beats re-sorting ~73k values
      rows = rows.concat(added);
      for (const p of prem) sortedInsert(histPremiums, p);
    }
    histMin = histPremiums[0];
    histMax = histPremiums[histPremiums.length - 1];
  }
  const series = rows.map((r) => ({ ts: r.ts, premium: r.premium_close, skhy: r.skhy_close }));
  const krwSeries = (await getKrw().catch(() => [])).filter((k) => k.ts >= LISTING); // real 000660 intraday (Naver fchart)
  const band = rows
    .filter((r) => r.mean != null && r.std != null)
    .map((r) => ({ ts: r.ts, mean: r.mean!, upper: r.mean! + 2 * r.std!, lower: r.mean! - 2 * r.std! }));
  const sigs = (await getSignals(200)) as Signal[];
  chart.setOption(
    buildOption(series, krwSeries, band, sigs.map((s) => ({ ts: s.ts, premium: s.premium_pct, level: s.level })), currentZoom()),
  );
  renderSignals(sigs);
}

function renderPercentile(current: number) {
  const el = document.getElementById("pct");
  if (!el || histPremiums.length === 0) return;
  const p = percentile(histPremiums, current);
  el.textContent = `${t().hist} P${p.toFixed(0)} · ${t().range} ${histMin.toFixed(1)}–${histMax.toFixed(1)}%`;
  el.style.color = p >= 90 ? "#B91C1C" : p <= 10 ? "#2979FF" : "#0A0A0A";
}

async function poll() {
  try {
    const l = (await getLatest()) as Latest | null;
    const now = Math.floor(Date.now() / 1000);
    if (l) {
      document.getElementById("premium")!.textContent = fmtPct(l.premium_pct);
      renderPercentile(l.premium_pct);
      const badge = document.getElementById("badge")!;
      badge.textContent = l.level || "—";
      badge.className = l.level ? `badge badge-${l.level}` : "badge";
      document.getElementById("state")!.textContent = marketState(l.market_state);
      document.getElementById("conn")!.textContent = connectionLabel(l.ts, now) + " · " + fmtTime(l.ts);
      (document.getElementById("legs") as HTMLElement).innerHTML =
        `<tr><td>SKHY</td><td>$${l.skhy_usd}</td></tr><tr><td>000660</td><td>₩${l.krw_price}</td></tr>` +
        `<tr><td>USD/KRW</td><td>${l.usdkrw}${l.fx_stale ? " (stale)" : ""}</td></tr>` +
        `<tr><td>underlying</td><td>$${l.underlying_usd}</td></tr>`;
    }
  } catch (e) {
    const conn = document.getElementById("conn");
    if (conn) conn.textContent = "STALE (retrying)";
  } finally {
    setTimeout(poll, document.hidden ? 5000 : 1000);
  }
}

addEventListener("resize", () => chart.resize());
document.getElementById("langtoggle")?.addEventListener("click", () => {
  lang = lang === "zh-CN" ? "en" : "zh-CN";
  localStorage.setItem("lang", lang);
  applyI18n();
});
applyI18n();
loadHistory().catch((e) => console.error("history load failed:", e));
poll();
setInterval(() => {
  loadHistory().catch(() => {});
}, 60000); // refresh chart + signal log + distribution every 60s. Only the first
// call scans the full history; the rest ask for `ts > lastTs`, which is one 1m
// row — and the minute-quantised URLs let the CDN absorb even those.
